import { useState, useEffect } from "react";
import { db, auth, storage } from "@/lib/firebase";
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, getDoc, onSnapshot, orderBy, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Image as ImageIcon, Upload, Trash2, X } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { dualWriteJobPhoto, dualDeleteJobPhoto, READ_MODE } from "@/lib/firestore-helpers";

interface Photo {
  id: string;
  image_url: string;
  description: string | null;
  photo_type: string | null;
  createdAt: string;
  uploaded_by: string;
  uploader_name?: string;
}

interface JobPhotosProps {
  jobId: string;
  contractorId: string;
}

export const JobPhotos = ({ jobId, contractorId }: JobPhotosProps) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [photoDescription, setPhotoDescription] = useState("");
  const [photoType, setPhotoType] = useState<string>("progress");
  const { toast } = useToast();

  useEffect(() => {
    fetchPhotos();
  }, [jobId, contractorId]);

  const fetchPhotos = () => {

    // Use READ_MODE to determine which structure to query
    const collectionPath = READ_MODE === 'new'
      ? `contractors/${contractorId}/jobs/${jobId}/photos`
      : 'job_photos';

    const constraints = READ_MODE === 'new'
      ? [orderBy("createdAt", "desc")]
      : [where("job_id", "==", jobId), orderBy("createdAt", "desc")];

    const q = query(collection(db, collectionPath), ...constraints);

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const photosData = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          
          // Fetch uploader name
          let uploaderName = "Unknown";
          try {
            const userDoc = await getDoc(doc(db, "users", data.uploaded_by));
            uploaderName = userDoc.exists() ? userDoc.data().fullName || "Unknown" : "Unknown";
          } catch (error) {
            console.error("Error fetching uploader name:", error);
          }
          
          return {
            id: docSnap.id,
            ...data,
            uploader_name: uploaderName,
          } as Photo;
        })
      );

      setPhotos(photosData);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a file smaller than 10MB",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const user = auth.currentUser;
    if (!user) {
      toast({
        title: "Not authenticated",
        description: "Please log in to upload photos",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      // Upload to Storage
      const fileName = `${Date.now()}-${selectedFile.name}`;
      const filePath = `jobs/${jobId}/photos/${fileName}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, selectedFile);
      const imageUrl = await getDownloadURL(storageRef);

      // Create job photo using dual-write helper
      const photoId = doc(collection(db, "job_photos")).id;
      await dualWriteJobPhoto(contractorId, jobId, photoId, {
        image_url: imageUrl,
        description: photoDescription || null,
        photo_type: photoType,
        uploaded_by: user.uid,
        createdAt: Timestamp.now(),
      });

      toast({
        title: "Photo uploaded",
        description: "Photo has been uploaded successfully",
      });

      // Reset form
      setSelectedFile(null);
      setPhotoDescription("");
      setPhotoType("progress");
      setShowUploadDialog(false);
    } catch (error: any) {
      console.error("❌ Error uploading photo:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload photo",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photo: Photo) => {
    if (!confirm("Are you sure you want to delete this photo?")) return;

    try {
      // Delete from Storage
      const storageRef = ref(storage, photo.image_url);
      await deleteObject(storageRef);

      // Delete from Firestore using dual-delete helper
      await dualDeleteJobPhoto(contractorId, jobId, photo.id);

      toast({
        title: "Photo deleted",
        description: "Photo has been deleted successfully",
      });

      setSelectedPhoto(null);
    } catch (error: any) {
      console.error("❌ Error deleting photo:", error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete photo",
        variant: "destructive",
      });
    }
  };

  const getPhotoTypeColor = (type: string | null) => {
    switch (type) {
      case 'before': return 'default';
      case 'progress': return 'secondary';
      case 'after': return 'default';
      case 'estimate': return 'outline';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Permit Photos</h3>
          <Button onClick={() => setShowUploadDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Photo
          </Button>
        </div>

        {photos.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No photos uploaded yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowUploadDialog(true)}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload First Photo
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {photos.map((photo) => (
              <Card 
                key={photo.id} 
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setSelectedPhoto(photo)}
              >
                <CardContent className="p-0">
                  <div className="relative aspect-video overflow-hidden rounded-t-lg">
                    <img
                      src={photo.image_url}
                      alt={photo.description || 'Permit photo'}
                      className="object-cover w-full h-full"
                    />
                    {photo.photo_type && (
                      <Badge 
                        className="absolute top-2 right-2 capitalize"
                        variant={getPhotoTypeColor(photo.photo_type)}
                      >
                        {photo.photo_type}
                      </Badge>
                    )}
                  </div>
                  <div className="p-4 space-y-2">
                    {photo.description && (
                      <p className="text-sm text-foreground line-clamp-2">
                        {photo.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {photo.createdAt && format(
                        photo.createdAt.toDate ? photo.createdAt.toDate() : new Date(photo.createdAt),
                        'MMM dd, yyyy'
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      By {photo.uploader_name}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Photo</label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              {selectedFile && (
                <p className="text-sm text-muted-foreground mt-2">
                  Selected: {selectedFile.name}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Type</label>
              <Select value={photoType} onValueChange={setPhotoType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">Before</SelectItem>
                  <SelectItem value="progress">Progress</SelectItem>
                  <SelectItem value="after">After</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Description (optional)</label>
              <Textarea
                value={photoDescription}
                onChange={(e) => setPhotoDescription(e.target.value)}
                placeholder="Add a description..."
                disabled={uploading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUploadDialog(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Detail Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <span>{selectedPhoto.description || 'Permit Photo'}</span>
                  {selectedPhoto.photo_type && (
                    <Badge 
                      variant={getPhotoTypeColor(selectedPhoto.photo_type)}
                      className="capitalize"
                    >
                      {selectedPhoto.photo_type}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleDelete(selectedPhoto)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedPhoto(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <img
                src={selectedPhoto.image_url}
                alt={selectedPhoto.description || 'Permit photo'}
                className="w-full rounded-lg"
              />
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {selectedPhoto.createdAt && format(
                    selectedPhoto.createdAt.toDate ? selectedPhoto.createdAt.toDate() : new Date(selectedPhoto.createdAt),
                    'MMMM dd, yyyy h:mm a'
                  )}
                </div>
                <span>•</span>
                <span>Uploaded by {selectedPhoto.uploader_name}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};
